import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { GitDependencyReference } from "@octopusdeploy/octopus-server-client";
import { HasGitPersistenceSettings } from "@octopusdeploy/octopus-server-client";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useGitCredentialsFromContext, useRefreshGitCredentialsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import { useOptionalProjectContext } from "~/areas/projects/context";
import type { TemplateValuesSectionComponent, TemplateValuesSectionProps } from "~/components/Actions/helmChartUpgrade/HelmTemplateValuesDrawer";
import type { GitRepositoryTemplateValuesSource } from "~/components/Actions/helmChartUpgrade/TemplateValuesSource";
import { GitRepositorySource } from "~/components/Actions/helmChartUpgrade/TemplateValuesSource";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { SimpleGitRepositorySourceSelector } from "~/components/GitRepositorySource/SimpleGitRepositorySourceSelector";
import { VariableLookupText } from "~/components/form/VariableLookupText";
import { ExpandableFormSection, Note, Summary } from "~/components/form/index";
import StringHelper from "~/utils/StringHelper/index";
const DefaultYamlFileLocation = "values.yaml";
export const GitRepositoryTemplateValuesSection = forwardRef<TemplateValuesSectionComponent, TemplateValuesSectionProps<GitRepositoryTemplateValuesSource>>((props: TemplateValuesSectionProps<GitRepositoryTemplateValuesSource>, ref) => {
    const projectContext = useOptionalProjectContext();
    const project = projectContext?.state.model;
    const [valuesFilePaths, setValuesFilePaths] = useState<string>(props.editingTemplateValuesSource?.ValuesFilePaths || "");
    const [gitDependencyReference, setGitDependencyReference] = useState<GitDependencyReference>(props.editingGitDependency || {
        Name: "",
        RepositoryUri: "",
        DefaultBranch: "main",
        GitCredentialType: "Library",
        FilePathFilters: [],
    });
    const gitCredentials = useGitCredentialsFromContext();
    const refreshGitCredentials = useRefreshGitCredentialsFromContext();
    const isProjectVersionControlled = project?.IsVersionControlled && HasGitPersistenceSettings(project.PersistenceSettings);
    const supportsProjectRepository = isProjectVersionControlled && !props.isProcessTemplateProcess;
    const [gitRepositorySource, setGitRepositorySource] = useState<GitRepositorySource>(props.editingTemplateValuesSource?.GitRepositorySource ? props.editingTemplateValuesSource?.GitRepositorySource : GitRepositorySource.External);
    const validateExternalGitRepositorySource = (): string | null => {
        if (StringHelper.isNullOrWhiteSpace(gitDependencyReference.RepositoryUri)) {
            return "A repository URI must be defined";
        }
        if (gitDependencyReference.GitCredentialType === "Library" && !gitDependencyReference.GitCredentialId) {
            return "A library Git credential must be selected";
        }
        if (StringHelper.isNullOrWhiteSpace(gitDependencyReference.DefaultBranch)) {
            return "A default branch must be defined";
        }
        return null;
    };
    const validateProjectGitRepositorySource = (): string | null => {
        if (!supportsProjectRepository) {
            return "This project is not configured for version control. Please configure version control settings first.";
        }
        return null;
    };
    useImperativeHandle(ref, () => ({
        validateAndSave: () => {
            if (!valuesFilePaths) {
                return "At least one values file path must be defined";
            }
            switch (gitRepositorySource) {
                case GitRepositorySource.External:
                    const externalValidationError = validateExternalGitRepositorySource();
                    if (externalValidationError) {
                        return externalValidationError;
                    }
                    return {
                        valuesSource: {
                            Type: "GitRepository",
                            GitRepositorySource: GitRepositorySource.External,
                            GitDependencyName: gitDependencyReference.Name,
                            ValuesFilePaths: valuesFilePaths,
                        },
                        gitDependency: gitDependencyReference,
                    };
                case GitRepositorySource.Project:
                    const projectValidationError = validateProjectGitRepositorySource();
                    if (projectValidationError) {
                        return projectValidationError;
                    }
                    return {
                        valuesSource: {
                            Type: "GitRepository",
                            GitRepositorySource: GitRepositorySource.Project,
                            GitDependencyName: "",
                            ValuesFilePaths: valuesFilePaths,
                        },
                        // For name generation, will not be saved to git dependency list
                        gitDependency: {
                            Name: "",
                            RepositoryUri: "",
                            DefaultBranch: "",
                            GitCredentialType: "Project",
                            FilePathFilters: [],
                        },
                    };
                default:
                    return "Invalid repository source selected";
            }
        },
    }));
    const valueFilePathsSummary = () => {
        if (!StringHelper.isNullOrWhiteSpace(valuesFilePaths)) {
            return Summary.summary("Paths to values files has been provided");
        }
        return Summary.placeholder("No paths to values files has been provided");
    };
    const gitRepositorySourceSummary = () => {
        switch (gitRepositorySource) {
            case GitRepositorySource.Project:
                if (!supportsProjectRepository) {
                    return Summary.placeholder("Project repository not supported");
                }
                return Summary.summary(`Project repository: ${project?.PersistenceSettings?.Url || "Unknown"}`);
            case GitRepositorySource.External:
                return Summary.summary("External repository");
            default:
                return Summary.placeholder("No repository source selected");
        }
    };
    return (<>
            {supportsProjectRepository && isFeatureToggleEnabled("ProjectHelmTemplateValuesFeatureToggle") && (<ExpandableFormSection errorKey="GitRepositorySource" title="Repository Source" summary={gitRepositorySourceSummary()} help="Select where the git repository is configured" isExpandedByDefault={!props.editingTemplateValuesSource}>
                    <RadioButtonGroup accessibleName="GitRepositorySource" value={gitRepositorySource} onChange={setGitRepositorySource}>
                        <RadioButton accessibleName="Project" value={GitRepositorySource.Project} label="Project" disabled={!!props.editingTemplateValuesSource && gitRepositorySource !== GitRepositorySource.Project}/>
                        <RadioButton accessibleName="External" value={GitRepositorySource.External} label="External" disabled={!!props.editingTemplateValuesSource && gitRepositorySource !== GitRepositorySource.External}/>
                    </RadioButtonGroup>
                </ExpandableFormSection>)}

            {gitRepositorySource === GitRepositorySource.External && (<SimpleGitRepositorySourceSelector gitDependency={gitDependencyReference} onGitDependencyChanged={setGitDependencyReference} gitCredentials={gitCredentials} refreshGitCredentials={refreshGitCredentials} expandedByDefault={true} localNames={props.localNames} getFieldError={() => ""} errorKeyPrefix={"GitRepositoryTemplateValuesSection"} hideAddRefresh={props.isProcessTemplateProcess}/>)}
            <ExpandableFormSection errorKey="GitRepositoryTemplateValuesSectionPaths" isExpandedByDefault={true} title="Paths" summary={valueFilePathsSummary()} help="Specify the paths to values files">
                <VariableLookupText multiline localNames={props.localNames} value={valuesFilePaths} onChange={(path) => setValuesFilePaths(path)} placeholder={DefaultYamlFileLocation} label="Relative path to values files"/>
                <Note>
                    A newline-separated list of file names, relative to the repository root to be included as additional <code>--values</code> files. Variable replacement will be run on these files before they are used. Extended template and wildcard
                    syntax is supported. E.g., <em>values.{`#{Octopus.Environment.Name}`}.yaml</em>, <em>**\specific-folder\*.yaml</em>.
                </Note>
            </ExpandableFormSection>
        </>);
});
